import React, { useMemo, useCallback } from "react"
import { Box, Tooltip, Button } from "@mui/material"
import { Add } from "@mui/icons-material"
import classNames from "classnames"

import type { Filter, FilterOption as FilterOptionType, FilterValue } from "resources/filter-ons"
import { isFilterValid, LONG_SHORT } from "helpers/filters"

import { EMPTY_FILTER } from "./helpers"
import FilterRow from "./filter-row"

interface FilterSelectorsProps {
  filters: Filter[]
  formattedOptions: FilterOptionType[]
  setFilters: React.Dispatch<React.SetStateAction<Filter[]>>
  currency: string
  handleClose: () => void
  additionalFilterComponent: React.ReactNode
  forceShowAdditionalFilterComponent: boolean
  showAdditionalFilterComponentOnActiveFilterOnly?: boolean
  setAnchorElement?: (anchorElement: HTMLElement | null) => void
  setActiveSearchIndex?: (activeSearchIndex: number | null) => void
  updateFilters: (filters: Filter[]) => void
  handleSetFilterOn: (index: number, filterOn: FilterOptionType) => void
}
export default function FilterSelectors({ filters, formattedOptions, setFilters, currency, handleClose, additionalFilterComponent, forceShowAdditionalFilterComponent, showAdditionalFilterComponentOnActiveFilterOnly, setAnchorElement, setActiveSearchIndex, updateFilters, handleSetFilterOn }: FilterSelectorsProps) {
  const handleSearchSecurities = useCallback((index: number) => (anchorElement: HTMLElement | null) => {
    setAnchorElement?.(anchorElement)
    setActiveSearchIndex?.(index)
  }, [setAnchorElement, setActiveSearchIndex])

  const updateFilterAtIndex = useCallback((index: number, updateFn: (filter: Filter) => Filter) => {
    setFilters((currentFilters: Filter[]) =>
      currentFilters.map((filter, filterIndex) =>
        filterIndex === index ? updateFn(filter) : filter,
      ),
    )
  }, [setFilters])

  const handleSetOperator = useCallback((index: number, operator: Filter["operator"]) => {
    updateFilterAtIndex(index, filter => ({ ...filter, operator, values: [] }))
  }, [updateFilterAtIndex])

  const handleSetValues = useCallback((index: number, ...newValues: Filter["values"]) => {
    updateFilterAtIndex(index, filter => {
      const maxLength = Math.max(filter.values.length, newValues.length)
      const mergedValues = Array.from({ length: maxLength }, (_, i) =>
        newValues[i] !== undefined ? newValues[i] : filter.values[i],
      )
      return { ...filter, values: mergedValues }
    })
  }, [updateFilterAtIndex])

  const handleSetAllValues = useCallback((index: number, ...values: Filter["values"]) => {
    updateFilterAtIndex(index, filter => ({ ...filter, values }))
  }, [updateFilterAtIndex])

  const handleRemoveAllFilters = useCallback(() => {
    updateFilters([])
    handleClose()
  }, [updateFilters, handleClose])

  const handleRemoveFilter = useCallback((index: number) => {
    const isLastExistingFilter = filters && filters.length === 1 && filters[index]?.isExisting

    if (isLastExistingFilter && !forceShowAdditionalFilterComponent) {
      handleRemoveAllFilters()
      return
    }

    setFilters((currentFilters: Filter[]) => {
      const newFilters = currentFilters.filter((_, filterIndex) => filterIndex !== index)

      if (newFilters.length === 0) {
        return EMPTY_FILTER
      }

      return newFilters
    })
  }, [filters, forceShowAdditionalFilterComponent, handleRemoveAllFilters, setFilters])

  const handleClearSecurities = useCallback((index: number) => {
    updateFilterAtIndex(index, filter => ({ ...filter, values: [] }))
  }, [updateFilterAtIndex])

  const handleDeleteSecurity = useCallback((index: number, item: FilterValue) => {
    const shouldKeepValue = (value: Filter["values"][number]) => {
      if (typeof value === "number" || typeof item === "number") {
        return value !== item
      }

      if (typeof value !== "object" || value === null) {
        return true
      }

      if ("assetClass" in value && value.assetClass === "index") {
        return value.indexId !== item.indexId
      }

      const valueTicker = "ticker" in value ? value.ticker : undefined
      const valueMic = "mic" in value ? value.mic : undefined

      return valueTicker !== item.ticker || valueMic !== item.mic
    }

    updateFilterAtIndex(index, filterObj => ({
      ...filterObj,
      values: filterObj.values.filter(shouldKeepValue),
    }))
  }, [updateFilterAtIndex])

  const handleAddAnotherFilter = useCallback(() => {
    setFilters((currentFilters: Filter[]) => [...currentFilters, ...EMPTY_FILTER])
  }, [setFilters])

  const isAddButtonDisabled = useMemo(() => {
    if (filters.length === 0) return false
    const lastFilter = filters[filters.length - 1]
    return !isFilterValid(lastFilter)
  }, [filters])

  const hasValidFilters = useMemo(() =>
    filters.some(filter => filter.filterOn && isFilterValid(filter)),
  [filters])

  const isFilterSelected = useCallback((option: FilterOptionType): boolean => {
    if (option.type === LONG_SHORT) {
      return filters.some(filter =>
        filter.filterOn && filter.filterOn.type === LONG_SHORT,
      )
    }
    return filters.some(filter =>
      filter.filterOn && filter.filterOn.value === option.value && filter.filterOn.type === option.type,
    )
  }, [filters])

  return (
    <div className="components_add-filter-new-filter-selectors">
      <div className="filter-header sticky-header">
        Filters
      </div>
      <div className="filter-components">
        <div className="filter-content-wrapper">
          <div className="filter-content">
            <Box flexGrow={1} display="flex" flexDirection="column" gap={1}>
              {filters.map((filter, index) => {
                const isUnsupported = filter.isUnsupported
                const FilterRowComponent = <FilterRow
                  {...{
                    filter,
                    index,
                    formattedOptions,
                    handleSetFilterOn,
                    handleSetOperator,
                    handleSetValues,
                    handleSetAllValues,
                    handleSearchSecurities,
                    handleClearSecurities,
                    handleDeleteSecurity,
                    currency,
                    isFilterSelected,
                    handleRemoveFilter,
                  }}
                />

                return isUnsupported ? (
                  <Tooltip key={`${index}-${filter.id}`} title="The filter could not be found" arrow>
                    {FilterRowComponent}
                  </Tooltip>
                ) : <React.Fragment key={`${index}-${filter.id}`}>
                  {FilterRowComponent}
                </React.Fragment>
              })}
            </Box>
          </div>
        </div>
        <div className={classNames("add-another-wrapper", { hidden: isAddButtonDisabled })}>
          <Button
            onClick={handleAddAnotherFilter}
            variant="text"
            color="primary"
            startIcon={<Add />}
            className="add-another-btn"
          >
            Add another filter
          </Button>
        </div>
        <div className="secondary-filters" onClick={e => e.stopPropagation()}>
          {(showAdditionalFilterComponentOnActiveFilterOnly && hasValidFilters) || forceShowAdditionalFilterComponent
            ? additionalFilterComponent
            : null}
        </div>
      </div>
    </div>
  )
}
